import { Injectable } from '@angular/core';
import {
  AgoraModel,
  AlertaCaixaAbertoModel,
  ComparativoPdvModel,
  ConferenciaResumoModel,
  ControleCaixaModel,
  CurvaHorariaModel,
  DiferencaOperadorModel,
  FechamentoDetalheItemModel,
  FormaPagamentoOperacaoModel,
  KpisOperacaoModel,
  MovimentoModel,
  RankingOperadorPdvModel,
  VendaCaixaModel,
} from '@app/data';
import { ApiService } from '@app/gateway';
import type { DashPeriodQueryPayload } from '@app/pages/dashboard/shared';

/** Body shared by every period-based Frente de Caixa endpoint (no status filter). */
export type FrenteCaixaPeriodPayload = Pick<DashPeriodQueryPayload, 'idAccounts' | 'startDate' | 'endDate'>;

export interface FrenteCaixaAgoraPayload {
  idAccounts: string[];
}

export interface FrenteCaixaAlertaCaixaAbertoPayload {
  idAccounts: string[];
  limiteHoras?: number;
}

export interface FrenteCaixaVendasCaixaPayload {
  idAccounts: string[];
  idPdvCaixa: number;
}

export interface FrenteCaixaControleCaixaPayload extends FrenteCaixaPeriodPayload {
  idPdv?: number;
  idPdvCaixa?: number;
  ignorarPeriodo?: boolean;
  pagina?: number;
}

export interface FrenteCaixaRankingOperadorPayload extends FrenteCaixaPeriodPayload {
  limit?: number;
}

/**
 * Frente de Caixa dashboard API: live PDV snapshot, period closing, session history and cash movements.
 */
@Injectable({ providedIn: 'root' })
export class FrenteCaixaService {
  constructor(private readonly apiService: ApiService) {}

  public async agora(data: FrenteCaixaAgoraPayload): Promise<AgoraModel> {
    try {
      const path: string = `pdv/agora`;
      const result: AgoraModel = await this.apiService.post<AgoraModel>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load open PDV sessions.';
      throw new Error(message, { cause: e });
    }
  }

  public async alertaCaixaAberto(data: FrenteCaixaAlertaCaixaAbertoPayload): Promise<AlertaCaixaAbertoModel[]> {
    try {
      const path: string = `pdv/alertaCaixaAberto`;
      const result: AlertaCaixaAbertoModel[] = await this.apiService.post<AlertaCaixaAbertoModel[]>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load open PDV session alerts.';
      throw new Error(message, { cause: e });
    }
  }

  public async conferenciaResumo(data: FrenteCaixaPeriodPayload): Promise<ConferenciaResumoModel> {
    try {
      const path: string = `pdv/conferenciaResumo`;
      const result: ConferenciaResumoModel = await this.apiService.post<ConferenciaResumoModel>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load Conferência de Caixa.';
      throw new Error(message, { cause: e });
    }
  }

  public async fechamentoDetalhe(data: FrenteCaixaPeriodPayload): Promise<FechamentoDetalheItemModel[]> {
    try {
      const path: string = `pdv/fechamentoDetalhe`;
      const result: FechamentoDetalheItemModel[] = await this.apiService.post<FechamentoDetalheItemModel[]>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load Conferência de Caixa.';
      throw new Error(message, { cause: e });
    }
  }

  public async diferencaOperador(data: FrenteCaixaPeriodPayload): Promise<DiferencaOperadorModel[]> {
    try {
      const path: string = `pdv/diferencaOperador`;
      const result: DiferencaOperadorModel[] = await this.apiService.post<DiferencaOperadorModel[]>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load Diferença de Caixa por Operador.';
      throw new Error(message, { cause: e });
    }
  }

  public async vendasCaixa(data: FrenteCaixaVendasCaixaPayload): Promise<VendaCaixaModel[]> {
    try {
      const path: string = `pdv/vendasCaixa`;
      const result: VendaCaixaModel[] = await this.apiService.post<VendaCaixaModel[]>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load vendas do caixa.';
      throw new Error(message, { cause: e });
    }
  }

  public async movimento(data: FrenteCaixaPeriodPayload): Promise<MovimentoModel> {
    try {
      const path: string = `pdv/movimento`;
      const result: MovimentoModel = await this.apiService.post<MovimentoModel>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load Movimento de Caixa.';
      throw new Error(message, { cause: e });
    }
  }

  public async controleCaixa(data: FrenteCaixaControleCaixaPayload): Promise<ControleCaixaModel> {
    try {
      const path: string = `pdv/controleCaixa`;
      const result: ControleCaixaModel = await this.apiService.post<ControleCaixaModel>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load Controle de Caixa.';
      throw new Error(message, { cause: e });
    }
  }

  public async kpisPeriodo(data: FrenteCaixaPeriodPayload): Promise<KpisOperacaoModel> {
    try {
      const path: string = `pdv/kpisPeriodo`;
      const result: KpisOperacaoModel = await this.apiService.post<KpisOperacaoModel>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load KPIs de operação do PDV.';
      throw new Error(message, { cause: e });
    }
  }

  public async formaPagamento(data: FrenteCaixaPeriodPayload): Promise<FormaPagamentoOperacaoModel[]> {
    try {
      const path: string = `pdv/formaPagamento`;
      const result: FormaPagamentoOperacaoModel[] = await this.apiService.post<FormaPagamentoOperacaoModel[]>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load mix de formas de pagamento do PDV.';
      throw new Error(message, { cause: e });
    }
  }

  public async rankingOperador(data: FrenteCaixaRankingOperadorPayload): Promise<RankingOperadorPdvModel[]> {
    try {
      const path: string = `pdv/rankingOperador`;
      const result: RankingOperadorPdvModel[] = await this.apiService.post<RankingOperadorPdvModel[]>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load ranking de operador de caixa.';
      throw new Error(message, { cause: e });
    }
  }

  public async curvaHoraria(data: FrenteCaixaPeriodPayload): Promise<CurvaHorariaModel> {
    try {
      const path: string = `pdv/curvaHoraria`;
      const result: CurvaHorariaModel = await this.apiService.post<CurvaHorariaModel>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load curva horária do PDV.';
      throw new Error(message, { cause: e });
    }
  }

  public async comparativo(data: FrenteCaixaPeriodPayload): Promise<ComparativoPdvModel> {
    try {
      const path: string = `pdv/comparativo`;
      const result: ComparativoPdvModel = await this.apiService.post<ComparativoPdvModel>({ path, data });
      return result;
    } catch (e: unknown) {
      const message: string = e instanceof Error ? e.message : 'Failed to load comparativo do PDV.';
      throw new Error(message, { cause: e });
    }
  }
}
